import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Landing from '@/pages/Landing';
import Checkout from '@/pages/Checkout';
import CheckoutSuccess from '@/pages/CheckoutSuccess';
import CheckoutCancelled from '@/pages/CheckoutCancelled';
import AdminLogin from '@/pages/admin/Login';
import AdminLayout from '@/pages/admin/Layout';
import Dashboard from '@/pages/admin/Dashboard';
import Stores from '@/pages/admin/Stores';
import Products from '@/pages/admin/Products';
import Transactions from '@/pages/admin/Transactions';
import Invoices from '@/pages/admin/Invoices';
import Recovery from '@/pages/admin/Recovery';
import Tutorials from '@/pages/admin/Tutorials';
import Snippet from '@/pages/admin/Snippet';
import Sync from '@/pages/admin/Sync';
import StripeAccounts from '@/pages/admin/StripeAccounts';
import VivaAccounts from '@/pages/admin/VivaAccounts';
import PayzenAccounts from '@/pages/admin/PayzenAccounts';
import StripeMonitor from '@/pages/admin/StripeMonitor';
import Batmobile from '@/pages/admin/Batmobile';
import Oracle from '@/pages/admin/Oracle';
import Vitrines from '@/pages/admin/Vitrines';
import CheckoutDesign from '@/pages/admin/CheckoutDesign';
import Hosting from '@/pages/admin/Hosting';
import Analytics from '@/pages/admin/Analytics';
import Users from '@/pages/admin/Users';
import Subscriptions from '@/pages/admin/Subscriptions';
import Clients from '@/pages/admin/Clients';
import Vault from '@/pages/admin/Vault';
import PaymentLinks from '@/pages/admin/PaymentLinks';
import PromoCodes from '@/pages/admin/PromoCodes';
import Sharingan from '@/pages/admin/Sharingan';
import SharinganProject from '@/pages/admin/SharinganProject';
import AdsStudio from '@/pages/admin/AdsStudio';
import AdsProject from '@/pages/admin/AdsProject';
import PayLink from '@/pages/PayLink';
import Subscribe from '@/pages/Subscribe';
import CampaignLanding from '@/pages/CampaignLanding';
import PlatformOnlyRoute from '@/components/PlatformOnlyRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import Invite from '@/pages/portal/Invite';
import Portal from '@/pages/portal/Portal';
import Payouts from '@/pages/admin/Payouts';
import { getToken, getCurrentUser } from '@/lib/api';
import { homeFor } from '@/lib/base';

// /admin is the super admin's private entrance; everyone else lives under /invite.
const RequireAuth = ({ children }) => {
  const me = getCurrentUser();
  if (!getToken() || !me) return <Navigate to="/admin/login" replace />;
  if (me.role !== 'super_admin') return <Navigate to={homeFor(me)} replace />;
  return children;
};
const RequireInvite = ({ children }) => {
  const me = getCurrentUser();
  if (!getToken() || !me) return <Navigate to="/invite/login" replace />;
  if (me.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (me.role === 'guest') return <Navigate to="/invite/portail" replace />;
  return children;
};
const RequirePortal = ({ children }) => {
  const me = getCurrentUser();
  if (!getToken() || !me) return <Navigate to="/invite/login" replace />;
  if (me.role !== 'guest' && me.role !== 'super_admin') return <Navigate to={homeFor(me)} replace />;
  return children;
};

const workspaceRoutes = <>
  <Route index element={<Dashboard />} />
  <Route path="analytics" element={<Analytics />} />
  <Route path="stripe-monitor" element={<StripeMonitor />} />
  <Route path="batmobile" element={<Batmobile />} />
  <Route path="oracle" element={<Oracle />} />
  <Route path="stripe-accounts" element={<StripeAccounts />} />
  <Route path="viva-accounts" element={<VivaAccounts />} />
  <Route path="payzen-accounts" element={<PayzenAccounts />} />
  <Route path="vitrines" element={<Vitrines />} />
  <Route path="hosting" element={<Hosting />} />
  <Route path="users" element={<Users />} />
  <Route path="stores" element={<Stores />} />
  <Route path="checkout-design" element={<CheckoutDesign />} />
  <Route path="products" element={<Products />} />
  <Route path="transactions" element={<Transactions />} />
  <Route path="payouts" element={<Payouts />} />
  <Route path="invoices" element={<Invoices />} />
  <Route path="payment-links" element={<PaymentLinks />} />
  <Route path="promo-codes" element={<PromoCodes />} />
  <Route path="sharingan" element={<Sharingan />} />
  <Route path="sharingan/:projectId" element={<SharinganProject />} />
  <Route path="sharingan/:projectId/:tab" element={<SharinganProject />} />
  <Route path="ads" element={<AdsStudio />} />
  <Route path="ads/:projectId" element={<AdsProject />} />
  <Route path="ads/:projectId/:tab" element={<AdsProject />} />
  <Route path="recovery" element={<Recovery />} />
  <Route path="tutoriels" element={<Tutorials />} />
  <Route path="snippet" element={<Snippet />} />
  <Route path="sync" element={<Sync />} />
  <Route path="subscriptions" element={<Subscriptions />} />
  <Route path="clients" element={<Clients />} />
  <Route path="vault" element={<Vault />} />
</>;

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
      <Routes>
        <Route path="/" element={<PlatformOnlyRoute><Landing /></PlatformOnlyRoute>} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
        <Route path="/checkout/v/:vitrineId" element={<Checkout />} />
        <Route path="/checkout/s/:shopifyId" element={<Checkout />} />
        <Route path="/checkout/:productId" element={<Checkout />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/pay/:linkId" element={<PayLink />} />
        <Route path="/subscribe/:planId" element={<Subscribe />} />
        <Route path="/c/:slug" element={<PlatformOnlyRoute><CampaignLanding /></PlatformOnlyRoute>} />
        <Route path="/admin/login" element={<AdminLogin zone="admin" />} />
        <Route path="/invite/login" element={<AdminLogin zone="invite" />} />
        <Route path="/invite/activation/:token" element={<Invite />} />
        <Route path="/invitation/:token" element={<Invite />} />
        <Route path="/invite/portail" element={<RequirePortal><Portal /></RequirePortal>} />
        <Route path="/portail" element={<Navigate to="/invite/portail" replace />} />
        <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>{workspaceRoutes}</Route>
        <Route path="/invite" element={<RequireInvite><AdminLayout /></RequireInvite>}>{workspaceRoutes}</Route>
        <Route path="*" element={<PlatformOnlyRoute><Navigate to="/" replace /></PlatformOnlyRoute>} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
